import { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check } from '@mui/icons-material';
import { LoadingButtonProps } from '@mui/lab';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, BoxProps, Button, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  useUpdateVendorMutation,
  useVendorQuery,
  useVendorsQuery,
  Vendor,
  VendorFragmentFragment,
  VendorUpdateInput,
} from '../../../api/gql';
import { FullPageLoader } from '../../../components';
import { FormInput, FormInputProps } from '../../../components/form/FormInput';
import { FormSelectProps } from '../../../components/form/FormSelect';
import { useAresSearch } from '../../../hooks/useAresSearch';
import { notify } from '../../../utils/notification';
import { vendorValidationSchema } from '../../../utils/validationSchemas';
import { useSafeNavigation } from '../../../hooks/useSafeNavigation';

export const EditVendor = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { safeNavigate } = useSafeNavigation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useVendorQuery({
    id: id ?? '',
  });

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({ message: t('query.error.default') });
    }
  }, [error, t]);

  const { mutate, isPending: isMutating } = useUpdateVendorMutation({
    onSuccess: (res) => {
      notify.positive({ message: t('vendor.mutation.success') });
      // invalidate customers query as vendor changed
      queryClient.invalidateQueries({ queryKey: useVendorsQuery.getKey() });

      if (id) {
        // update vendor query with mutation result
        queryClient.setQueryData(useVendorQuery.getKey({ id }), {
          vendor: res.updateVendor,
        });
      }

      safeNavigate('..');
    },
    onError: () => {
      notify.negative({ message: t('vendor.mutation.error') });
    },
  });

  const handleSubmit = (data: Omit<VendorFragmentFragment, 'id'>) => {
    if (!data.name || !id) {
      throw new Error('Name or ID are required fields');
    }

    const vendorDto: VendorUpdateInput = {
      id: id,
      name: data.name,
      address: data.address,
      vatId: data.vatId,
      taxId: data.taxId,
    };

    mutate({ vendor: vendorDto });
  };

  return (
    <Box p={4} minHeight={598}>
      <Typography variant={'h1'}>{t('editContact')}</Typography>
      {isLoading ? <FullPageLoader /> : null}
      <VendorForm
        vendor={data?.vendor}
        onSubmit={handleSubmit}
        submitButtonProps={{
          loading: isMutating,
        }}
        mt={4}
      />
    </Box>
  );
};

type VendorFormProps = {
  onSubmit: (data: Omit<VendorFragmentFragment, 'id'>) => void;
  readonly?: boolean;
  vendor?: VendorFragmentFragment;
  submitButtonProps?: LoadingButtonProps;
} & BoxProps;
const VendorForm = ({ readonly, vendor, onSubmit, submitButtonProps, ...rest }: VendorFormProps) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { defaultValues },
    getValues,
    setValue,
    reset,
    handleSubmit,
  } = useForm<Omit<Vendor, 'id' | '__typename'>>({
    resolver: yupResolver(vendorValidationSchema),
  });

  const {
    search,
    state: { isPending },
  } = useAresSearch();

  const onAresSearch = async () => {
    const vatId = getValues('vatId');

    if (!vatId) {
      // should not happen due to validation
      return;
    }

    const res = await search({
      vatId: vatId,
    });

    if (res.queryAres) {
      const address = [res.queryAres.streetAddress, res.queryAres.zip, res.queryAres.city, res.queryAres.country]
        .filter((e) => e)
        .join('\n');

      res.queryAres.name ? setValue('name', res.queryAres.name) : null;
      res.queryAres.taxId ? setValue('taxId', res.queryAres.taxId) : null;
      address ? setValue('address', address) : null;

      notify.positive({ message: t('ares.prefill.success') });
      return;
    }

    notify.warning({ message: t('ares.prefill.error') });
  };

  const formInputSharedProps = useMemo(
    () =>
      ({
        size: 'medium',
        variant: 'standard',
        autoComplete: 'off',
        InputProps: {
          readOnly: readonly,
          disableUnderline: readonly,
        },
      }) satisfies Partial<FormSelectProps & FormInputProps>,
    [readonly],
  );

  // Populate Form if data are not yet loaded on component mount
  useEffect(() => {
    if (vendor && !defaultValues) {
      reset(vendor);
    }
  }, [vendor, defaultValues, reset]);

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid xs={12} md={6}>
            <FormInput
              {...formInputSharedProps}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              control={control}
              name={'vatId'}
              label={t('vatId') + '*'}
            />
          </Grid>
          <Grid xs={12} md={6} alignSelf={'center'}>
            <LoadingButton loading={isPending} fullWidth variant={'outlined'} onClick={onAresSearch}>
              {t('updateFromAres')}
            </LoadingButton>
          </Grid>
          <Grid xs={12}>
            <FormInput {...formInputSharedProps} control={control} name={'taxId'} label={t('taxId')} />
          </Grid>
          <Grid xs={12}>
            <FormInput {...formInputSharedProps} control={control} name={'name'} label={t('businessName') + '*'} />
          </Grid>
          <Grid xs={12}>
            <FormInput
              {...formInputSharedProps}
              control={control}
              multiline={true}
              minRows={3}
              name={'address'}
              label={t('streetAddress')}
            />
          </Grid>
        </Grid>

        <Stack direction={'column'} gap={2} mt={6}>
          <LoadingButton
            {...submitButtonProps}
            startIcon={<Check />}
            fullWidth
            variant={'contained'}
            size={'large'}
            type={'submit'}
          >
            {t('saveContact')}
          </LoadingButton>
          <Button fullWidth variant={'text'} type={'button'} component={Link} to={'..'}>
            {t('cancel')}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
